import { Box, Typography } from '@components';
import { MetricGroupBreakdownResponse } from '@interfaces';
import { monthNamesShortNL } from '@utils';
import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { chartColors } from '../hardware/AssetAgeChart';

type Props = {
  data: MetricGroupBreakdownResponse;
  onClick?: (payload: any) => void;
  title?: string;
  currency?: string; // default EUR
  className?: string;
  // optioneel: toon expliciet welke maand dit is (als je parent die al weet)
  monthLabel?: string; // "jan 2025"
  loadingGroup?: string;
};

function formatCurrency(value: number, currency = 'EUR') {
  return new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value ?? 0);
}

function formatPct(p: number) {
  return new Intl.NumberFormat('nl-BE', {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(p);
}

function toTitle(s: string) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function stableColorForKey(key: string) {
  let h = 8;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return chartColors[h % chartColors.length];
}

export default function MetricGroupBreakdownCard({
  data,
  title = 'Opsplitsing per categorie',
  currency = 'EUR',
  className = '',
  monthLabel,
  onClick,
  loadingGroup,
}: Props) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const handleRowClick = (r: any) => {
    setSelectedKey(r.key);

    onClick?.({
      year: r.year,
      month: r.month,
      metricGroup: r.metricGroup,
      // phoneNumbers: ['0472636584'],
    });
  };

  const mapped = useMemo(() => {
    const rows = Array.isArray(data?.results) ? [...data.results] : [];

    rows.sort((a, b) =>
      (a.metric_group || '').localeCompare(b.metric_group || '')
    );

    const total = rows.reduce((s, r) => s + Number(r['sum(`value`)'] ?? 0), 0);

    const normalized = rows.map((r) => {
      const value = Number(r['sum(`value`)'] ?? 0);
      const pct = total > 0 ? value / total : 0;

      const key = `${r.year}-${r.month}-${r.metric_group}`;
      const label = toTitle(r.metric_group.replaceAll('_', ' '));

      return {
        key,
        year: r.year,
        month: r.month,
        metricGroup: r.metric_group,
        label,
        value,
        pctOfTotal: pct,
        total,
        raw: r,
        color: stableColorForKey(r.metric_group),
      };
    });

    // sorteer “meest belangrijk bovenaan”
    normalized.sort((a, b) => b.value - a.value);

    const inferredMonthLabel =
      monthLabel ??
      (rows.length
        ? `${monthNamesShortNL[(rows[0].month - 1) % 12] ?? rows[0].month} ${rows[0].year}`
        : undefined);

    return { rows: normalized, total, inferredMonthLabel };
  }, [data, monthLabel]);

  const rows = mapped.rows;
  const total = mapped.total;
  const effectiveMonthLabel = mapped.inferredMonthLabel;

  return (
    <Box type="bordered" size="medium" gridSize="grid-1">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Typography tag="span" type="labelSemiBold">
            {title}
          </Typography>

          <div className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-50 tabular-nums">
            {formatCurrency(total, currency)}
          </div>

          {effectiveMonthLabel && (
            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Maand: <span className="font-medium">{effectiveMonthLabel}</span>
            </div>
          )}
        </div>

        <div className="text-xs text-slate-500 dark:text-slate-400">
          {rows.length} categorieën
        </div>
      </div>

      {/* Horizontale bar chart (bovenaan) */}
      <div className="mt-4">
        <div className="relative h-[180px] rounded-2xl border border-slate-200/70 dark:border-slate-800 p-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ top: 8, right: 12, bottom: 8, left: 8 }}
              barCategoryGap={10}
            >
              <CartesianGrid
                strokeDasharray="4 4"
                horizontal={false}
                strokeOpacity={0.25}
              />

              <XAxis
                type="number"
                tickLine={false}
                axisLine={false}
                fontSize={12}
                tickFormatter={(v) => {
                  // compact: 1k, 1.2k (zelfde stijl als in je andere chart)
                  if (v >= 1000) {
                    const k = v / 1000;
                    return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
                  }
                  return String(v);
                }}
              />

              <YAxis
                type="category"
                dataKey="label"
                tickLine={false}
                axisLine={false}
                width={110}
                fontSize={12}
                tickFormatter={(v: string) =>
                  v?.length > 14 ? `${v.slice(0, 14)}…` : v
                }
              />

              <Tooltip
                cursor={{ fill: 'rgba(59,130,246,0.08)' }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload as any;

                  return (
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-md dark:border-slate-800 dark:bg-slate-950">
                      <div className="font-medium text-slate-900 dark:text-slate-50">
                        {p.label}
                      </div>
                      <div className="text-slate-600 dark:text-slate-300 tabular-nums">
                        {formatCurrency(p.value, currency)}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {formatPct(p.pctOfTotal)}
                      </div>
                    </div>
                  );
                }}
              />

              <Bar dataKey="value" radius={[10, 10, 10, 10]}>
                {rows.map((r: any) => {
                  const selected = selectedKey === r.key;
                  return (
                    <Cell
                      key={r.key}
                      cursor="pointer"
                      fill={r.color}
                      opacity={selected ? 1 : 0.85}
                      stroke={
                        selected ? 'rgba(59,130,246,0.9)' : 'rgba(0,0,0,0)'
                      }
                      strokeWidth={selected ? 2 : 0}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Subtle helper (optioneel, maar werkt goed op dashboards) */}
          {rows?.[0] && (
            <div className="pointer-events-none absolute left-3 bottom-2 text-[11px] text-slate-500 dark:text-slate-400">
              Grootste:{' '}
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {rows[0].label}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Table (onder de chart) */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200/70 dark:border-slate-800">
        <div className="flex items-center justify-between bg-slate-50 px-3 py-2 dark:bg-slate-900/40">
          <Typography tag="span" type="small">
            Categoriën
          </Typography>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            % van totaal
          </div>
        </div>

        <table className="w-full text-sm">
          <thead className="text-slate-600 dark:text-slate-300">
            <tr className="border-b border-slate-200/70 dark:border-slate-800">
              <th className="px-3 py-2 text-left font-medium">
                <Typography type="label" tag="span">
                  Groep
                </Typography>
              </th>
              <th className="px-3 py-2 text-right font-medium">
                <Typography type="label" tag="span">
                  Bedrag
                </Typography>
              </th>
              <th className="px-3 py-2 text-right font-medium">
                <Typography type="label" tag="span">
                  %
                </Typography>
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  className="px-3 py-4 text-center text-slate-500 dark:text-slate-400"
                >
                  Geen data
                </td>
              </tr>
            ) : (
              rows.map((r: any) => {
                const selected = selectedKey === r.key;
                const barW = `${Math.round((r.pctOfTotal ?? 0) * 100)}%`;

                return (
                  <tr
                    key={r.key}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleRowClick(r)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleRowClick(r);
                    }}
                    className={[
                      'transition-colors outline-none cursor-pointer',
                      selected
                        ? 'bg-blue-50/70 dark:bg-blue-500/10'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-900/40',
                    ].join(' ')}
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: r.color }}
                        />
                        <div className="font-medium text-slate-900 dark:text-slate-50">
                          {r.label}
                        </div>
                      </div>

                      {/* mini aandeel bar */}
                      <div className="mt-1 h-1.5 w-full rounded-full bg-slate-200/70 dark:bg-slate-800">
                        <div
                          className={`h-1.5 rounded-full ${loadingGroup === r.label && 'animate-pulse'}`}
                          style={{
                            width: barW,
                            backgroundColor: r.color,
                            opacity: 0.65,
                          }}
                        />
                      </div>
                    </td>

                    <td className="px-3 py-2 text-right tabular-nums text-slate-900 dark:text-slate-50">
                      {formatCurrency(r.value, currency)}
                    </td>

                    <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">
                      {formatPct(r.pctOfTotal)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>

          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-slate-200/70 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/30">
                <td className="px-3 py-2 text-left text-xs font-medium text-slate-600 dark:text-slate-300">
                  Totaal
                </td>
                <td className="px-3 py-2 text-right text-sm font-semibold text-slate-900 dark:text-slate-50 tabular-nums">
                  {formatCurrency(total, currency)}
                </td>
                <td className="px-3 py-2" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Box>
  );
}
